import React from "react";
import { motion } from "framer-motion";
import { Target, Eye, Compass, ShieldCheck } from "lucide-react";
import { Seo } from "@/lib/seo";
import { PageHero } from "@/components/layout/PageHero";
import { Reveal, Stagger, staggerItem } from "@/components/motion/Reveal";
import { PlatformCta } from "@/components/sections/PlatformCta";
import { SectionHeading, Overline } from "@/components/ui/primitives";
import { useLang } from "@/i18n/LanguageContext";
import { deepLocalize } from "@/i18n/translate";

const values = [
  { icon: ShieldCheck, title: "Trust", text: "Governance you can prove — every control linked to durable evidence." },
  { icon: Compass, title: "Sovereignty", text: "Aligned with European digital sovereignty and jurisdiction requirements." },
  { icon: Target, title: "Engineering", text: "Precision, resilience and operational excellence over marketing promises." },
  { icon: Eye, title: "Transparency", text: "Explainability and human oversight embedded across the lifecycle." },
];

const chapters = [
  {
    n: "01",
    label: "Mission",
    title: "Operationalise verifiable AI governance.",
    text: "We translate high-level principles into repeatable controls, clear ownership and durable evidence — so the organisations that matter most can deploy artificial intelligence responsibly and prove it.",
  },
  {
    n: "02",
    label: "Vision",
    title: "A Europe where AI is auditable, resilient and sovereign by design.",
    text: "We envision critical institutions operating advanced AI with complete traceability, continuous compliance and operational continuity — trusted by citizens, regulators and partners.",
  },
];

export default function About() {
  const { lang, tr } = useLang();
  const vals = deepLocalize(values, lang);
  const chaps = deepLocalize(chapters, lang);
  return (
    <>
      <Seo
        title={tr("About AstroLife™ — Sovereign AI, Mission & Vision")}
        description={tr("AstroLife™ builds sovereign, auditable and resilient AI infrastructure aligned with European values, digital sovereignty and verifiable governance.")}
        path="/about"
        keywords="About AstroLife, Sovereign AI, European Digital Sovereignty, Responsible AI, AI Governance"
      />

      <PageHero
        overline={tr("About AstroLife™")}
        title={tr("Sovereign AI infrastructure, engineered for trust.")}
        subtitle={tr("Building trustworthy, auditable and resilient AI ecosystems aligned with European values, digital sovereignty and verifiable governance.")}
        breadcrumbs={[{ label: tr("About") }]}
      />

      {/* Mission / Vision manifesto */}
      <section className="bg-navy py-24 lg:py-32" id="mission">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Overline>{tr("Mission & Vision")}</Overline>
          <div className="mt-12 space-y-16">
            {chaps.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.05}>
                <div className="grid items-start gap-6 md:grid-cols-12">
                  <div className="md:col-span-4">
                    <span className="font-heading text-7xl md:text-8xl font-black text-outline">{c.n}</span>
                    <p className="mt-2 text-xs font-bold uppercase tracking-[0.2em] text-astro-light">{c.label}</p>
                  </div>
                  <div className="md:col-span-8">
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-white">{c.title}</h2>
                    <p className="mt-4 max-w-2xl text-lg leading-relaxed text-silver/70">{c.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Image band */}
      <section className="relative border-y border-navy-border">
        <div className="relative h-72 md:h-96 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1574848296471-28f79a036f79"
            alt="Modern European institutional architecture"
            loading="lazy"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/50 to-navy/30" />
          <div className="absolute inset-0 flex items-end">
            <div className="mx-auto w-full max-w-7xl px-5 lg:px-8 pb-10">
              <p className="max-w-2xl font-heading text-2xl md:text-3xl font-bold text-white">
                {tr("Designed for strategic European environments requiring traceability, compliance and operational continuity.")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-navy py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading overline={tr("Our Values")} title={tr("Principles that guide every engineering decision.")} />
          <Stagger className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {vals.map((v) => (
              <motion.div key={v.title} variants={staggerItem} className="rounded-2xl border border-navy-border bg-navy-surface p-7">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-astro-blue/10 text-astro-light">
                  <v.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 font-heading text-lg font-semibold text-white">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-silver/60">{v.text}</p>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      <PlatformCta />
    </>
  );
}
